<template>
  <!-- ▼Main Container -->
  <div class="boss-print-content">
    <applicant-print-title
      :entry-company-name="entryCompanyName"
      :user-name="userName"
      :is-duplicated-entries="isDuplicatedEntries"
      :entry-form-type="entryFormType"
      :pathway="pathway"
    />
    <div class="boss-layout boss-layout-gap-between">
      <div
        class="boss-resume-sheet-title pega-resume-sheet-title"
        data-test="selection-title"
      >
        {{ selectionTitle }}
      </div>
    </div>
    <applicant-print-selection
      :entry-id="Number($route.params.entryId)"
      :is-casual-interview-request="isCasualInterviewRequest"
      :selections="selections"
      :selected-responsible-staff-name="selectedResponsibleStaffName"
      :remarks="remarks"
    />
    <p>アプローチ状況</p>
    <approached-summary
      :approached-summaries="approachedSummaries"
      :is-print-preview="true"
    />
    <p>直近のスカウト文面</p>
    <div
      v-if="hasScoutMessage"
      class="boss-message-wrap"
    >
      <applicant-message-history-scout
        :body="scoutMessage.body"
        :subject="scoutMessage.subject"
        :date="scoutMessage.date"
        :job="scoutMessage.job"
        :expiration-date="scoutMessage.expirationDate"
        :special-favors="scoutMessage.specialFavors"
      />
    </div>
    <span
      v-else
      class="pega-no-scout-label"
    >
      直近のスカウトなし
    </span>
    <p class="boss-page-break" />
    <applicant-detail-resume
      :resume="resume"
      :header-user-name="userName"
      :entry-company-name="entryCompanyName"
      :is-duplicated-entries="isDuplicatedEntries"
      :entry-form="entryFormType"
      :pathway="pathway"
    />
    <p class="boss-page-break" />
    <applicant-detail-career-resume
      :entry-id="Number($route.params.entryId)"
      :header-user-name="userName"
      :entry-company-name="entryCompanyName"
      :is-duplicated-entries="isDuplicatedEntries"
      :entry-form="entryFormType"
      :pathway="pathway"
      :resume-updated-at="resume.updatedDate"
      :experienced-occupations="experiencedOccupations"
      :experienced-businesses="experiencedBusinesses"
      :career="career"
      :skills="skills"
      :is-light-entry="isLightEntry"
      :is-casual-interview-request="isCasualInterviewRequest"
      :skill-note="skillNote"
    />
  </div>
  <!-- ▲Main Container -->
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import ApplicantPrintTitle from '@/components/molecules/ApplicantPrintTitle'
import ApplicantDetailResume from '@/components/organisms/ApplicantDetailResume'
import ApproachedSummary from '@/components/molecules/ApproachedSummary'
import ApplicantDetailCareerResume from '@/components/organisms/ApplicantDetailCareerResume'
import ApplicantMessageHistoryScout from '@/components/molecules/ApplicantMessageHistoryScout'
import ApplicantPrintSelection from '@/components/organisms/ApplicantPrintSelection'
import _ from 'lodash'
import { parseHierarchicalObjects } from '@/lib/parse'

export default {
  components: {
    ApplicantPrintTitle,
    ApplicantDetailResume,
    ApproachedSummary,
    ApplicantDetailCareerResume,
    ApplicantMessageHistoryScout,
    ApplicantPrintSelection
  },
  data () {
    return {
      resume: {},
      scoutMessage: {},
      userName: '',
      entryCompanyName: '',
      isDuplicatedEntries: false,
      entryFormType: '',
      pathway: null,
      isCasualInterviewRequest: false,
      approachedSummaries: [],
      selections: [],
      selectedResponsibleStaffName: '',
      remarks: '',
      experiencedOccupations: [],
      experiencedBusinesses: [],
      career: {},
      skills: [],
      isLightEntry: false,
      skillNote: ''
    }
  },
  computed: {
    hasScoutMessage () {
      return !_.isEmpty(this.scoutMessage)
    },
    selectionTitle () {
      return (this.isCasualInterviewRequest) ? '話を聞きたい応募（カジュアル面談）' : '選考状況'
    },
    ...mapGetters([
      'skillsMaster'
    ])
  },
  async created () {
    await this.fetchEntry()
    await this.storeMaster('skills')
    // skillはapi取得完了後のデータ整形処理で時間がかかりpdf作成時の描画が間に合わなかったためPromise.allには含めず先に取得しておく
    await this.fetchSkills()
    const callApis = [
      this.storeMaster('specialFavors'),
      this.fetchScoutMessage(),
      this.fetchResume(),
      this.fetchApproachedSummary(),
      this.fetchResponsibleStaff(),
      this.fetchRemarks(),
      this.fetchExperiencedOccupations(),
      this.fetchExperiencedBusinesses(),
      this.fetchCareer(),
      this.fetchSkillNote()
    ]

    // 話を聞きたい応募の場合は選考情報は表示しない為、selection_phases取得しない
    if (!this.isCasualInterviewRequest) {
      callApis.push(this.fetchSelections())
    }

    Promise.all(
      callApis
    ).then(async () => {
      await this.$nextTick()
      window.print()
    })
  },
  methods: {
    fetchScoutMessage () {
      return this.$http.secured.get(`/entry/view/entries/${this.$route.params.entryId}/history/scouts`)
        .then(response => this.$set(this, 'scoutMessage', _.head(response.data) || {}))
    },
    fetchEntry () {
      return this.$http.secured.get(`/entry/view/entries/${this.$route.params.entryId}`).then(response => {
        this.$set(this, 'userName', _(response.data).at(['user.lastname', 'user.firstname']).join(' '))
        this.$set(this, 'entryCompanyName', _.get(response.data, 'companyName', ''))
        this.$set(this, 'isDuplicatedEntries', _.get(response.data, 'isDuplicatedEntries', false))
        this.$set(this, 'entryFormType', _.get(response.data, 'entryFormType', ''))
        this.$set(this, 'pathway', _.get(response.data, 'pathway', null))
        this.$set(this, 'isCasualInterviewRequest', _.get(response.data, 'isCasualInterviewRequest', false))
        this.$set(this, 'isLightEntry', _.get(response.data, 'isLightEntry', false))
      })
    },
    fetchResume () {
      return this.$http.secured.get(`/entry/view/entries/${this.$route.params.entryId}/resume`).then(response => {
        this.$set(this, 'resume', response.data)
      })
    },
    fetchApproachedSummary () {
      return this.$http.secured.get(`/entry/view/entries/${this.$route.params.entryId}/approached_jobs`).then(response => {
        this.$set(this, 'approachedSummaries', response.data)
      })
    },
    fetchSelections () {
      return this.$http.secured.get(`/entry/view/entries/${this.$route.params.entryId}/selection_phases`)
        .then(response => this.$set(this, 'selections', response.data))
    },
    fetchResponsibleStaff () {
      return this.$http.secured.get(`/entry/view/entries/${this.$route.params.entryId}/responsible_staff`).then(response => {
        this.selectedResponsibleStaffName = _.defaultTo(_.get(response.data, 'name'), 'ー')
      })
    },
    fetchRemarks () {
      return this.$http.secured.get(`/entry/view/entries/${this.$route.params.entryId}/remarks`).then(response => {
        this.remarks = response.data
      })
    },
    fetchExperiencedOccupations () {
      return this.$http.secured.get(`/entry/view/entries/${this.$route.params.entryId}/resume/experienced_occupations`).then(response => {
        this.$set(this, 'experiencedOccupations', response.data)
      })
    },
    fetchExperiencedBusinesses () {
      return this.$http.secured.get(`/entry/view/entries/${this.$route.params.entryId}/resume/experienced_businesses`).then(response => {
        this.$set(this, 'experiencedBusinesses', response.data)
      })
    },
    fetchCareer () {
      return this.$http.secured.get(`/entry/view/entries/${this.$route.params.entryId}/resume/career`).then(response => {
        this.$set(this, 'career', response.data)
      })
    },
    fetchSkills () {
      return this.$http.secured.get(`/entry/view/entries/${this.$route.params.entryId}/resume/skills`).then(response => {
        this.$set(this, 'skills', _(response.data).map(o => {
          return _.assign(o, _.pick(_.find(parseHierarchicalObjects(this.skillsMaster), { id: o.id }), 'path'))
        }).value())
      })
    },
    fetchSkillNote () {
      return this.$http.secured.get(`/entry/view/entries/${this.$route.params.entryId}/resume/skill_note`).then(response => {
        this.$set(this, 'skillNote', response.data)
      })
    },
    ...mapActions([
      'storeMaster'
    ])
  }
}
</script>
